import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import moment from 'moment';

import { userSchemas, distributorSchemas } from 'schemas';
import {
    useGetDistributorCompaniesQuery,
    useGetInvestmentFundsQuery,
    useGetRolesQuery,
    useGetShareSubcategoriesQuery,
    useGetUsersQuery,
} from 'api/main';
import {
    ORDER_BY,
    RoleNames,
    UserEnrichments,
    investorClassifications,
    roleCategories,
} from 'utils/constants';
import { parseFullName, parseRoleByProfile } from 'utils/helpers';
import { useUserRoleMatchCategory } from 'utils/hooks';
import { userSelectors } from 'reducers';
import { texts } from 'utils/languages/fr';

import {
    ActionButton,
    CancelButton,
    CustomDatePicker,
    CustomSelect,
    CustomTextField,
    Loader,
} from 'components';
import { Box, Collapse, InputAdornment, MenuItem, Paper, Typography } from '@mui/material';

const inputSx = { marginBottom: 0, width: '200px' };

const getDefaultFilters = obj => ({
    roles: obj?.role || 'all',
    entryDateStart: obj?.entryDateStart ? moment(obj.entryDateStart) : null,
    entryDateEnd: obj?.entryDateEnd ? moment(obj.entryDateEnd) : null,
    minSum: obj?.minSum || '',
    maxSum: obj?.maxSum || '',
    minAdmissionFee: obj?.minAdmissionFee || '',
    maxAdmissionFee: obj?.maxAdmissionFee || '',
    subcategory: obj?.subcategory || 'all',
    product: obj?.product || 'all',
    company: obj?.company || 'all',
    investorClassification: obj?.investorClassification || 'all',
    subscriptionCreatedBy: obj?.subscriptionCreatedBy || 'all',
    tag: obj?.tag || '',
});

const PaymentFilters = ({ open, applyFilters, loading }) => {
    const userIsDistributor = useUserRoleMatchCategory(roleCategories.ADMIN_DISTRIBUTORS);
    const isServicer = useUserRoleMatchCategory(roleCategories.SERVICERS);
    const internalUser = useSelector(userSelectors.getInternalUser);

    const { data: roles = [], isLoading: rolesLoader } = useGetRolesQuery();
    const { data: fundList = [], isFetching: fundsLoader } = useGetInvestmentFundsQuery({
        visible: 1,
        distributorId: isServicer
            ? undefined
            : internalUser.subscriptions?.[0]?.distributorCompany?.id,
    });

    const [customFilters, setCustomFilters] = useState(getDefaultFilters());

    const { data: shareSubcategories = [], isLoading: subcategoriesLoader } =
        useGetShareSubcategoriesQuery(
            {
                'shareCategory.investmentFund':
                    customFilters.product === 'all' ? [+customFilters.product] : [],
            },
            { skip: !customFilters.product }
        );
    const {
        data: { userList: subscriptionCreatedBys = [] } = { userList: [] },
        isLoading: subscriptionCreatedBysLoader,
    } = useGetUsersQuery(
        userSchemas.getUsers({
            pagination: false,
            companyId: customFilters.company === 'all' ? undefined : customFilters.company,
            role: roles
                .filter(
                    r =>
                        r.name === RoleNames.COMMERCIALISATEUR ||
                        r.name === RoleNames.ADMIN_CIF ||
                        r.name === RoleNames.PSI
                )
                .map(r => r.id),
            enrichWith: [UserEnrichments.ROLES, UserEnrichments.DISTRIBUTOR_COMPANY],
        }),
        { skip: !roles.length || !customFilters.company }
    );
    const {
        data: { distributors: distributorCompanies = [] } = { distributors: [] },
        isFetching: companyLoader,
    } = useGetDistributorCompaniesQuery(
        distributorSchemas.getDistributorList({
            pagination: false,
            orderBy: ORDER_BY.distributorCompanyName,
            oneOrMoreConventionsFullySigned: 1,
        })
    );

    const handleChange = useCallback(
        key => e => {
            setCustomFilters(prevState => ({
                ...prevState,
                subscriptionCreatedBy: key === 'company' ? 'all' : prevState.subscriptionCreatedBy,
                subcategory: key === 'product' ? 'all' : prevState.subcategory,
                [key]: e.target.value,
            }));
        },
        []
    );
    const handleDateChange = useCallback(
        key => newDate => {
            if (key.includes('Start')) {
                const endKey = key.replace('Start', 'End');
                if (customFilters[endKey] && newDate.isAfter(customFilters[endKey])) {
                    setCustomFilters(oldValue => ({ ...oldValue, [endKey]: newDate }));
                }
            }
            setCustomFilters(oldValue => ({ ...oldValue, [key]: newDate }));
        },
        [customFilters]
    );

    const handleCancel = useCallback(() => {
        let defaults = getDefaultFilters();
        setCustomFilters(defaults);
        applyFilters({ ...defaults, roles: [], product: [] });
    }, [applyFilters]);

    const handleApplyFilters = useCallback(() => {
        applyFilters({
            ...customFilters,
            product: customFilters.product === 'all' ? [] : [customFilters.product],
            roles:
                customFilters.roles === 'all' ? [] : parseRoleByProfile(customFilters.roles, roles),
        });
    }, [applyFilters, customFilters, roles]);

    return (
        <Collapse in={open} sx={{ '.MuiCollapse-wrapper': { marginBottom: 4 } }}>
            <Paper elevation={1} sx={{ width: '100%', padding: 2 }}>
                <Loader
                    loading={
                        companyLoader ||
                        fundsLoader ||
                        rolesLoader ||
                        subscriptionCreatedBysLoader ||
                        subcategoriesLoader
                    }
                >
                    <Box mb={2}>
                        <CustomSelect
                            value={customFilters.roles}
                            label={texts.PROFIL_INVESTISSEUR}
                            onChange={handleChange('roles')}
                            controlSx={inputSx}
                        >
                            <MenuItem value="all">{texts.ALL_1}</MenuItem>
                            <MenuItem value="PP">{texts.PERSONNE_PHYSIQUE}</MenuItem>
                            <MenuItem value="PM">{texts.PERSONNE_MORALE}</MenuItem>
                        </CustomSelect>
                    </Box>
                    <Typography variant="subtitle1">{texts.DATE_ENTREE}</Typography>
                    <Box display="flex" alignItems="flex-end" flexWrap="wrap" mb={2}>
                        <CustomDatePicker
                            label={texts.START_DATE}
                            controlSx={inputSx}
                            value={customFilters.entryDateStart}
                            onChange={handleDateChange('entryDateStart')}
                            maxDate={moment()}
                        />
                        <Box component="span" sx={{ marginBottom: 1, marginX: 2 }}>
                            -
                        </Box>
                        <CustomDatePicker
                            label={texts.END_DATE}
                            controlSx={inputSx}
                            value={customFilters.entryDateEnd}
                            onChange={handleDateChange('entryDateEnd')}
                            minDate={customFilters.entryDateStart || undefined}
                            maxDate={moment()}
                        />
                    </Box>
                    <Typography variant="subtitle1">{texts.MONTANT}</Typography>
                    <Box display="flex" alignItems="center" mb={2}>
                        <CustomTextField
                            formatNumber
                            placeholder={texts.MIN_SUM}
                            formControlProps={{ sx: inputSx }}
                            value={customFilters.minSum}
                            onChange={handleChange('minSum')}
                            endAdornment={<InputAdornment position="start">€</InputAdornment>}
                        />
                        <Box component="span" sx={{ marginX: 2 }}>
                            -
                        </Box>
                        <CustomTextField
                            formatNumber
                            placeholder={texts.MAX_SUM}
                            formControlProps={{ sx: inputSx }}
                            value={customFilters.maxSum}
                            onChange={handleChange('maxSum')}
                            endAdornment={<InputAdornment position="start">€</InputAdornment>}
                        />
                    </Box>
                    <Typography variant="subtitle1">{texts.ADMISSION_FEE}</Typography>
                    <Box display="flex" alignItems="center" mb={2}>
                        <CustomTextField
                            formatNumber
                            placeholder={texts.MIN_PERCENTAGE}
                            formControlProps={{ sx: inputSx }}
                            value={customFilters.minAdmissionFee}
                            onChange={handleChange('minAdmissionFee')}
                            endAdornment={<InputAdornment position="start">%</InputAdornment>}
                        />
                        <Box component="span" sx={{ marginX: 2 }}>
                            -
                        </Box>
                        <CustomTextField
                            formatNumber
                            placeholder={texts.MAX_PERCENTAGE}
                            formControlProps={{ sx: inputSx }}
                            value={customFilters.maxAdmissionFee}
                            onChange={handleChange('maxAdmissionFee')}
                            endAdornment={<InputAdornment position="start">%</InputAdornment>}
                        />
                    </Box>
                    <Box display="flex" flexWrap="wrap" mb={2} gap="16px">
                        <CustomSelect
                            value={customFilters.product}
                            label={texts.PRODUIT}
                            onChange={handleChange('product')}
                            controlSx={inputSx}
                        >
                            <MenuItem value="all">{texts.ALL_1}</MenuItem>
                            {fundList.map(fund => (
                                <MenuItem key={fund.id} value={fund.id}>
                                    {fund.name}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                        <CustomSelect
                            disabled={!customFilters.product}
                            value={customFilters.subcategory}
                            label={texts.CATEGORIE_DE_PARTS}
                            onChange={handleChange('subcategory')}
                            controlSx={inputSx}
                        >
                            <MenuItem value="all">{texts.ALL_2}</MenuItem>
                            {shareSubcategories.map(sc => (
                                <MenuItem key={sc.id} value={sc.id}>
                                    {sc.name}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                        {!userIsDistributor && (
                            <CustomSelect
                                value={customFilters.company}
                                label={texts.PARTNER}
                                onChange={handleChange('company')}
                                controlSx={inputSx}
                            >
                                <MenuItem value="all">{texts.ALL_1}</MenuItem>
                                {<MenuItem value={0}>{texts.PEQAN}</MenuItem>}
                                {distributorCompanies.map(({ 0: { id, corporateName } }) => (
                                    <MenuItem key={id} value={id}>
                                        {corporateName}
                                    </MenuItem>
                                ))}
                            </CustomSelect>
                        )}
                        <CustomSelect
                            value={customFilters.investorClassification}
                            label={texts.STATUT}
                            onChange={handleChange('investorClassification')}
                            controlSx={inputSx}
                        >
                            <MenuItem value="all">{texts.ALL_1}</MenuItem>
                            {Object.keys(investorClassifications).map(ic => (
                                <MenuItem key={ic} value={ic}>
                                    {investorClassifications[ic]}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                    </Box>
                    <Box display="flex" flexWrap="wrap" mb={2} gap="16px">
                        <CustomSelect
                            value={customFilters.subscriptionCreatedBy}
                            label={texts.CONSULTANT}
                            disabled={!customFilters.company}
                            onChange={handleChange('subscriptionCreatedBy')}
                            controlSx={inputSx}
                        >
                            <MenuItem value="all">{texts.ALL_1}</MenuItem>
                            {subscriptionCreatedBys.map(u => (
                                <MenuItem key={u.id} value={u.id}>
                                    {parseFullName(u)}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                        <CustomTextField
                            label={texts.COMPLEMENT}
                            placeholder={texts.COMPLEMENT}
                            formControlProps={{ sx: inputSx }}
                            value={customFilters.tag}
                            onChange={handleChange('tag')}
                        />
                    </Box>
                    <Box textAlign="right">
                        <CancelButton disabled={loading} onClick={handleCancel}>
                            {texts.CANCEL}
                        </CancelButton>
                        <ActionButton loading={loading} onClick={handleApplyFilters}>
                            {texts.APPLY}
                        </ActionButton>
                    </Box>
                </Loader>
            </Paper>
        </Collapse>
    );
};

PaymentFilters.propTypes = {
    open: PropTypes.bool.isRequired,
    applyFilters: PropTypes.func.isRequired,
    loading: PropTypes.bool,
};

export default PaymentFilters;
